import type { RefOrGetter } from "ls/common/types";
import type { MaybeRefOrGetter } from "vue";
import type { StyleRefUpdate, StyleUpdate } from "../interop/dotnetExports/bridge";
import type { Payload } from "../platform/edit";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { createSharedComposable } from "@vueuse/core";
import { useLastModifiedDate } from "ls/features/files/useTrackLastModifiedDate";
import { computed, toValue } from "vue";
import { normalizeGuid } from "@/common/lib/string";
import { onGradientModeGeometryChanged } from "../bridge/editor";
import { AxObjectType, getSelection, onSelectionChanged } from "../bridge/selection";
import { useDiskoDocumentReady } from "../composables/useDiskoDocumentReady";
import { defaultDotnetQueryOptions } from "../defaultDotnetQueryOptions";
import { focusCanvasOrEditor, getCurrentPage, getDiagramObject, getDiagramObject2, getDiagramObjectAt, getDiagramObjects, getDiagramObjects2, getDiagramSections, getTrackedDiagramObject } from "../interop/dotnetExports/bridge";
import { onEditApplied, onEditInProgress } from "../platform/edit";
import { updateDiagramEditorStyle, updateDiagramEditorStyleReference, updateTextEditorStyle, updateTextEditorStyleReference } from "../platform/styleCommands";
import { useDiagramTextEditorsStore } from "../store/textEditor";
import { useCurrentFileContext } from "../useCurrentFileContext";

export const queries = {
  diagram: (shortcut: string) => ["diagram", shortcut.toLowerCase()] as const,

  currentPage: (shortcut: string) => {
    return queryOptions({
      ...defaultDotnetQueryOptions,
      queryKey: [...queries.diagram(shortcut), "current-page"] as const,
      queryFn: getCurrentPage,
    });
  },

  selection: (shortcut: string) => {
    return queryOptions({
      ...defaultDotnetQueryOptions,
      queryKey: [...queries.diagram(shortcut), "selection"] as const,
      queryFn: getSelection,
    });
  },

  // selectionData: (shortcut: string) => {
  //   return queryOptions({
  //     ...defaultDotnetQueryOptions,
  //     queryKey: [...queries.diagram(shortcut), "selection-data"] as const,
  //     queryFn: getSelectionData,
  //   });
  // },

  sections: (shortcut: string) => {
    return queryOptions({
      ...defaultDotnetQueryOptions,
      queryKey: [...queries.diagram(shortcut), "sections"] as const,
      queryFn: getDiagramSections,
    });
  },

  diagramObject: (shortcut: string, id: string) => {
    return queryOptions({
      ...defaultDotnetQueryOptions,
      queryKey: [...queries.diagram(shortcut), "diagramObject", normalizeGuid(id)] as const,
      queryFn: () => {
        const diagramObject = getDiagramObject(id);
        if (!diagramObject) throw new Error(`Diagram object ${id} was not found.`);
        return diagramObject;
      },
    });
  },

  diagramObject2: (shortcut: string, id: string) => {
    return queryOptions({
      ...defaultDotnetQueryOptions,
      queryKey: [...queries.diagram(shortcut), "diagramObject2", normalizeGuid(id)] as const,
      queryFn: () => {
        const diagramObject = getDiagramObject2(id);
        if (!diagramObject) throw new Error(`Diagram object ${id} was not found.`);
        return diagramObject;
      },
    });
  },

  diagramObjectAt: (shortcut: string, x: number, y: number) => {
    return queryOptions({
      ...defaultDotnetQueryOptions,
      queryKey: [...queries.diagram(shortcut), "diagramObjectAt", { x, y }] as const,
      queryFn: () => {
        const diagramObject = getDiagramObjectAt(x, y);
        return diagramObject;
      },
    });
  },

  diagramObjects: (shortcut: string) => {
    return queryOptions({
      ...defaultDotnetQueryOptions,
      queryKey: [...queries.diagram(shortcut), "diagramObjects"] as const,
      queryFn: getDiagramObjects,
    });
  },

  diagramObjects2: (shortcut: string) => {
    return queryOptions({
      ...defaultDotnetQueryOptions,
      queryKey: [...queries.diagram(shortcut), "diagramObjects2"] as const,
      queryFn: getDiagramObjects2,
    });
  },

  trackedDiagramObjects: (shortcut: string) => [...queries.diagram(shortcut), "trackedDiagramObject"] as const,

  trackedDiagramObject: (shortcut: string, trackingId: string) => {
    return queryOptions({
      ...defaultDotnetQueryOptions,
      queryKey: [...queries.trackedDiagramObjects(shortcut), normalizeGuid(trackingId)] as const,
      queryFn: () => {
        const diagramObjectId = getTrackedDiagramObject(toValue(trackingId));
        if (!diagramObjectId) return null;

        const diagramObject = getDiagramObject(diagramObjectId);
        return diagramObject;
      },
    });
  },
};

export function useDiagramObjectQueriesInvalidation() {
  const queryClient = useQueryClient();
  const { shortcut, file } = useCurrentFileContext();
  const { update: updateLastModifiedDate } = useLastModifiedDate(file);

  const invalidateObjectQueries = (payload: Payload): void => {
    for (const editObjectId of payload.objectIds) {
      void queryClient.invalidateQueries(queries.diagramObject(shortcut.value, editObjectId));
    }

    // because currently there's no specific event to carefully invalidate tracked objects
    // we'll invalidate all tracked objects
    void queryClient.invalidateQueries({ queryKey: queries.trackedDiagramObjects(shortcut.value) });

    // also invalidate current selection
    void queryClient.invalidateQueries(queries.selection(shortcut.value));

    updateLastModifiedDate();
  };

  onEditApplied(invalidateObjectQueries);
  onEditInProgress(invalidateObjectQueries);
}

export function useDiagramObject(id: RefOrGetter<string>) {
  const { shortcut } = useCurrentFileContext();
  const documentReady = useDiskoDocumentReady();

  const options = computed(() => {
    return {
      ...queries.diagramObject(shortcut.value, toValue(id)),
      enabled: documentReady.value,
    };
  });
  const query = useQuery(options);
  return query;
}

export function useDiagramObject2(id: RefOrGetter<string>) {
  const { shortcut } = useCurrentFileContext();
  const documentReady = useDiskoDocumentReady();

  const options = computed(() => {
    return {
      ...queries.diagramObject2(shortcut.value, toValue(id)),
      enabled: documentReady.value,
    };
  });
  const query = useQuery(options);
  return query;
}

export function useDiagramObjectAt(x: RefOrGetter<number>, y: RefOrGetter<number>) {
  const { shortcut } = useCurrentFileContext();
  const documentReady = useDiskoDocumentReady();

  const options = computed(() => {
    return {
      ...queries.diagramObjectAt(shortcut.value, toValue(x), toValue(y)),
      enabled: documentReady.value,
    };
  });
  const query = useQuery(options);
  return query;
}

function useDiagramSelectionFn() {
  const { shortcut } = useCurrentFileContext();
  const queryClient = useQueryClient();
  const documentReady = useDiskoDocumentReady();

  onSelectionChanged(() => {
    void queryClient.invalidateQueries(queries.selection(shortcut.value));
  });

  onGradientModeGeometryChanged(() => {
    void queryClient.invalidateQueries(queries.selection(shortcut.value));
  });

  const options = computed(() => {
    return {
      ...queries.selection(shortcut.value),
      enabled: documentReady.value,
    };
  });
  const query = useQuery(options);

  return query.data;
}

export const useDiagramSelection = createSharedComposable(useDiagramSelectionFn);

function useCurrentSelectionFn() {
  const selection = useDiagramSelection();
  const textEditorsStore = useDiagramTextEditorsStore();

  const style = computed(() => {
    // if text editor is active, we want styles from the text editor to override styles from the selection
    if (textEditorsStore.activeEditorStyles) {
      return {
        ...selection.value?.style,
        ...textEditorsStore.activeEditorStyles,
      };
    }
    return selection.value?.style;
  });

  const actionFlags = computed(() => {
    return {
      // eslint-disable-next-line no-bitwise
      showImg: selection.value && AxObjectType.Image === (selection.value.type & AxObjectType.Image),
      showImgOnly: selection.value?.type === AxObjectType.Image,
      hasNonVectorImage: selection.value?.hasNonVectorImage,
      showShapeOnly: selection.value?.type === AxObjectType.Shape,

      // eslint-disable-next-line no-bitwise
      showEmbed: selection.value && AxObjectType.SmartLink === (selection.value.type & AxObjectType.SmartLink),
      showEmbedOnly: selection.value?.type === AxObjectType.SmartLink,

      // eslint-disable-next-line no-bitwise
      showFill: selection.value && (AxObjectType.Shape === (selection.value.type & AxObjectType.Shape) || AxObjectType.Frame === (selection.value.type & AxObjectType.Frame)),

      // eslint-disable-next-line no-bitwise
      showLine: selection.value && AxObjectType.Line === (selection.value.type & AxObjectType.Line),
      showLineOnly: selection.value?.type === AxObjectType.Line,

      // eslint-disable-next-line no-bitwise
      showConnector: selection.value && AxObjectType.Connector === (selection.value.type & AxObjectType.Connector),
      showConnectorOnly: selection.value?.type === AxObjectType.Connector,

      // eslint-disable-next-line no-bitwise
      showBorder: selection.value && (selection.value.type & AxObjectType.ShapeImageFormFields) > 0,

      // eslint-disable-next-line no-bitwise
      showText: selection.value && (selection.value.type & AxObjectType.ShapeLineConnectorFormFields) > 0,

      // eslint-disable-next-line no-bitwise
      showCard: selection.value && AxObjectType.Card === (selection.value.type & AxObjectType.Card),
      showCardOnly: selection.value?.type === AxObjectType.Card,

      // eslint-disable-next-line no-bitwise
      showSection: selection.value && AxObjectType.Section === (selection.value.type & AxObjectType.Section),
      showSectionOnly: selection.value?.type === AxObjectType.Section,

      // eslint-disable-next-line no-bitwise
      showPencil: selection.value && AxObjectType.PencilShape === (selection.value.type & AxObjectType.PencilShape),
      showPencilOnly: selection.value?.type === AxObjectType.PencilShape,

      // eslint-disable-next-line no-bitwise
      showShadow: selection.value && (selection.value.type & AxObjectType.ShapeLineImageFormFields) > 0,

      // eslint-disable-next-line no-bitwise
      showOpacity: selection.value && (selection.value.type & AxObjectType.ShapeLineImageFormFields) > 0,

      singleSelection: selection.value?.selectedItems.length === 1,
    };
  });

  return {
    selection,
    style,
    actionFlags,
    isNone: computed(() => selection.value?.type === AxObjectType.None),
  };
}

export const useCurrentSelection = createSharedComposable(useCurrentSelectionFn);

export function doFocusCanvasOrEditor() {
  focusCanvasOrEditor();
}

export interface UseUpdateStyleOptions {
  /**
   * Controls focus behavior when applying update. If true, the editor will be focused after applying the update. This is useful for updates that are triggered from the editor itself, to prevent focus loss. For updates triggered from outside of the editor, this should typically be false to avoid unexpected focus changes.
   *
   * defaults to `"text-editor"`
   */
  focus?: StyleUpdate["focus"];
}

export function useUpdateCurrentSelectionStyle(options?: UseUpdateStyleOptions) {
  const queryClient = useQueryClient();

  const store = useDiagramTextEditorsStore();

  const { shortcut } = useCurrentFileContext();
  const { mutate: updateStyle } = useMutation({
    mutationFn(update: StyleUpdate) {
      if (options?.focus !== undefined) {
        update.focus = options.focus;
      }

      if (store.activeEditor) {
        updateTextEditorStyle(store.activeEditor, update);
        return Promise.resolve();
      }
      updateDiagramEditorStyle(update);
      return Promise.resolve();
    },
    onSuccess() {
      if (!store.activeEditor) {
        void queryClient.invalidateQueries(queries.selection(shortcut.value));
      }
    },
  });

  const { mutate: updateStyleReference } = useMutation({
    mutationFn(update: StyleRefUpdate) {
      if (options?.focus !== undefined) {
        update.focus = options.focus;
      }

      if (store.activeEditor) {
        updateTextEditorStyleReference(store.activeEditor, update);
        return Promise.resolve();
      }
      updateDiagramEditorStyleReference(update);
      return Promise.resolve();
    },
    onSuccess() {
      if (!store.activeEditor) {
        void queryClient.invalidateQueries(queries.selection(shortcut.value));
      }
    },
  });

  return {
    updateStyle,
    updateStyleReference,
  };
}

export function useDiagramObjects() {
  const documentReady = useDiskoDocumentReady();
  const { shortcut } = useCurrentFileContext();

  const options = computed(() => {
    return {
      ...queries.diagramObjects(shortcut.value),
      enabled: documentReady.value,
    };
  });
  return useQuery(options);
}

export function useDiagramObjects2() {
  const documentReady = useDiskoDocumentReady();
  const { shortcut } = useCurrentFileContext();

  const options = computed(() => {
    return {
      ...queries.diagramObjects2(shortcut.value),
      enabled: documentReady.value,
    };
  });
  return useQuery(options);
}

export function useDiagramSections() {
  const documentReady = useDiskoDocumentReady();
  const { shortcut } = useCurrentFileContext();

  const options = computed(() => {
    return {
      ...queries.sections(shortcut.value),
      enabled: documentReady.value,
    };
  });
  return useQuery(options);
}

export function useTrackedDiagramObject(trackingId: MaybeRefOrGetter<string>) {
  const documentReady = useDiskoDocumentReady();
  const { shortcut } = useCurrentFileContext();

  const options = computed(() => {
    return {
      ...queries.trackedDiagramObject(shortcut.value, toValue(trackingId)),
      enabled: documentReady.value,
    };
  });
  return useQuery(options);
}
